import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { getSurface, getItemId, getConversationId, getUserEmail, getSenderInfo, getSubject, onItemChanged, getSettings, saveSettings, setMatterInternetHeader, saveDraftAndGetConversationId, getInternetMessageId, getInReplyToId } from './services/office';
import { hasToken, isTokenFresh, verifyToken, loginWithOfficeEmail, logout, isAdmin } from './services/auth';
import * as webhook from './services/webhook';
import Header from './components/Header';
import TabBar from './components/TabBar';
import StatusBar from './components/StatusBar';
import ReadingPane from './panels/ReadingPane';
import ComposePane from './panels/ComposePane';
import SettingsPane from './panels/SettingsPane';
import DashboardPane from './panels/DashboardPane';
import TriagePane from './panels/TriagePane';
import AdminPane from './panels/AdminPane';
import LoginScreen from './components/LoginScreen';
import { DEFAULT_SETTINGS } from './utils/constants';

// ── Global Add-in Context ──
export const AddinContext = createContext(null);

export function useAddin() {
    return useContext(AddinContext);
}

export default function App() {
    const [authenticated, setAuthenticated] = useState(false);
    const [authChecking, setAuthChecking] = useState(true);
    const [surface, setSurface] = useState('reading_pane');
    const [itemId, setItemId] = useState(null);
    const [conversationId, setConversationId] = useState(null);
    const [userEmail, setUserEmail] = useState('nick@ioliberum.com');
    const [senderInfo, setSenderInfo] = useState(null);
    const [subject, setSubject] = useState('');
    const [status, setStatus] = useState('Connected');
    const [statusLabel, setStatusLabel] = useState('N8N · Graph API Connected');

    // ── Settings State ──
    const [showSettings, setShowSettings] = useState(false);
    const [showDashboard, setShowDashboard] = useState(false);
    const [showTriage, setShowTriage] = useState(false);
    const [showAdmin, setShowAdmin] = useState(false);
    const [isAdminUser, setIsAdminUser] = useState(false);
    const [userSettings, setUserSettings] = useState(DEFAULT_SETTINGS);

    // ── Matter Lookup State ──
    const [linkedMatter, setLinkedMatter] = useState(null);
    const [lookupState, setLookupState] = useState('loading'); // loading | linked | suggested | picker | no_match | error
    const [lookupMatters, setLookupMatters] = useState([]);

    // Check auth on mount — three-step cascade:
    //  1. Token is locally fresh (no network call needed)
    //  2. Try silent re-auth using Outlook user identity
    //  3. Fall back to server-side verify (or show login form)
    useEffect(() => {
        async function checkAuth() {
            // Step 1: Token still has plenty of life left — accept it instantly.
            if (hasToken() && isTokenFresh()) {
                setAuthenticated(true);
                setIsAdminUser(isAdmin());
                setAuthChecking(false);
                return;
            }

            // Step 2: Token expired / missing — try silent login using the
            // Outlook user's email.
            const outlookEmail = getUserEmail();
            if (outlookEmail) {
                const silentOk = await loginWithOfficeEmail(outlookEmail);
                if (silentOk) {
                    setAuthenticated(true);
                    setIsAdminUser(isAdmin());
                    setAuthChecking(false);
                    return;
                }
            }

            // Step 3: Silent auth failed (unauthorized email or network issue).
            // If we still have a token, do a server-side verify as a last resort.
            if (hasToken()) {
                const valid = await verifyToken();
                if (valid) {
                    setAuthenticated(true);
                    setIsAdminUser(isAdmin());
                    setAuthChecking(false);
                    return;
                }
                logout(); // Token is stale — clear it
            }

            // Nothing worked — show the manual login form.
            setAuthChecking(false);
        }
        checkAuth();
    }, []);


    // Initialize Office context after auth
    useEffect(() => {
        if (!authenticated) return;

        let pollInterval = null;

        const loadContext = () => {
            // Clear any active polling interval first
            if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
            }

            const surf = getSurface();
            setSurface(surf);
            setItemId(getItemId());
            
            let initialConvId = getConversationId();
            let resolvedFromCache = false;

            // Check if we can carry over the linked matter from read to compose using inReplyTo
            if (surf === 'compose') {
                const inReplyTo = getInReplyToId();
                if (inReplyTo) {
                    const cached = localStorage.getItem(`parent_matter_${inReplyTo}`);
                    if (cached) {
                        try {
                            const { matter, conversationId: cachedConvId } = JSON.parse(cached);
                            setLinkedMatter(matter);
                            setLookupState('linked');
                            setStatusLabel(`Linked: ${matter.matter_number || matter.number || 'Matter'}`);
                            
                            if (cachedConvId) {
                                initialConvId = cachedConvId;
                            }
                            resolvedFromCache = true;

                            // Stamp the internet header immediately so N8N fires on send
                            setMatterInternetHeader(matter.id || matter.matter_id).catch(err => {
                                console.error('Failed to set internet header on cached auto-linked compose item:', err);
                            });
                        } catch (e) {
                            console.error('Failed to parse cached parent matter:', e);
                        }
                    }
                }
            }

            if (!resolvedFromCache) {
                setLinkedMatter(null);
                setLookupState('loading');
            }

            setConversationId(initialConvId);
            setUserEmail(getUserEmail());
            setSubject(getSubject());

            getSenderInfo().then((info) => {
                if (info) setSenderInfo(info);
            });

            // If we are in compose/reply mode and conversationId is not loaded yet,
            // poll for it because Outlook can take a moment to populate it.
            if (surf === 'compose' && !initialConvId) {
                let retries = 0;
                pollInterval = setInterval(() => {
                    const polledConvId = getConversationId();
                    if (polledConvId) {
                        setConversationId(polledConvId);
                        clearInterval(pollInterval);
                        pollInterval = null;
                    } else if (retries >= 15) { // 3 seconds max
                        clearInterval(pollInterval);
                        pollInterval = null;
                    }
                    retries++;
                }, 200);
            }

            // Load settings
            const settings = getSettings();
            if (Object.keys(settings).length > 0) {
                // merge with defaults to ensure all keys exist
                setUserSettings({ ...DEFAULT_SETTINGS, ...settings });
            }
        };

        loadContext();

        // Listen for item changes (support for pinning)
        const cleanup = onItemChanged(() => {
            loadContext();
        });

        return () => {
            if (pollInterval) clearInterval(pollInterval);
            cleanup();
        };
    }, [authenticated]);

    // ── Matter Lookup — auto-fire after context is ready ──
    const performLookup = useCallback(async (currentItemId, currentConvId, currentUserEmail, currentSenderInfo) => {
        setLookupState('loading');
        setLinkedMatter(null);
        setLookupMatters([]);

        try {
            // Extract [matterId] from the subject line (e.g. "Re: Meeting notes [12345]")
            // The add-in stamps this tag via setMatterInternetHeader() when linking a matter in compose mode.
            // Sending it here lets N8N skip fuzzy matching and do a direct matter lookup by ID.
            const currentSubject = getSubject();
            const subjectMatch = currentSubject?.match(/\[(\d+)\]$/);
            const subjectMatterId = subjectMatch ? subjectMatch[1] : null;

            const result = await webhook.lookupMatter({
                outlookItemId: currentItemId,
                conversationId: currentConvId,
                userEmail: currentUserEmail,
                senderEmail: currentSenderInfo?.email || '',
                surface: getSurface(),
                subjectMatterId,
            });

            if (result?.status === 'linked' && result.matter) {
                // Direct hit — conversation already linked
                setLinkedMatter(result.matter);
                setLookupState('linked');
                setStatusLabel(`Linked: ${result.matter.matter_number || result.matter.number || 'Matter'}`);

                // Cache in localStorage if we have an internetMessageId (in read mode)
                const internetMessageId = getInternetMessageId();
                if (internetMessageId) {
                    localStorage.setItem(`parent_matter_${internetMessageId}`, JSON.stringify({
                        matter: result.matter,
                        conversationId: currentConvId,
                        timestamp: Date.now()
                    }));
                }

                // In Compose mode, stamp the internet header so N8N fires on send
                const currentSurf = getSurface();
                if (currentSurf === 'compose' || currentSurf === 'ComposePane') {
                    try {
                        await setMatterInternetHeader(result.matter.id || result.matter.matter_id);
                    } catch (err) {
                        console.error('Failed to set internet header on auto-linked compose item:', err);
                    }
                }
            } else if (result?.status === 'suggested' && result.matters?.length === 1) {
                // Single suggestion
                setLookupMatters(result.matters);
                setLookupState('suggested');
                setStatusLabel('Matter suggested — confirm to link');
            } else if (result?.status === 'picker' && result.matters?.length > 0) {
                // Multiple options
                setLookupMatters(result.matters);
                setLookupState('picker');
                setStatusLabel(`${result.matters.length} matters found — select one`);
            } else if (result?.status === 'no_match') {
                setLookupState('no_match');
                setStatusLabel('No matter found');
            } else {
                // Unexpected response shape — treat as no match
                setLookupState('no_match');
                setStatusLabel('No matter found');
            }
        } catch (err) {
            console.error('Matter lookup failed:', err);
            setLookupState('error');
            setStatusLabel('Matter lookup failed');
        }
    }, []);

    // Trigger lookup when Office context is populated
    useEffect(() => {
        if (!authenticated) return;

        // If we already have a linked matter (e.g. resolved from cache or manual link),
        // skip running the performance lookup entirely to avoid resetting the state.
        if (linkedMatter) {
            return;
        }

        // In compose mode, if we don't have a conversation ID yet (e.g. brand new email),
        // there is no existing conversation to look up — skip the auto-lookup and go straight
        // to no_match so the user sees the Search Matters prompt immediately.
        if (surface === 'compose' && !conversationId) {
            setLookupState('no_match');
            setStatusLabel('Link a matter to file this email');
            return;
        }

        // Wait for either itemId or senderInfo to be available (or timeout for dev mode)
        const timer = setTimeout(() => {
            performLookup(itemId, conversationId, userEmail, senderInfo);
        }, 300);
        return () => clearTimeout(timer);
    }, [authenticated, surface, itemId, conversationId, userEmail, senderInfo, performLookup]);

    // ── Link Matter — called when user confirms a matter selection ──
    const handleLinkMatter = useCallback(async (matter) => {
        // Immediately set as linked for responsive UI
        setLinkedMatter(matter);
        setLookupState('linked');
        setStatusLabel(`Linked: ${matter.matter_number || matter.number || 'Matter'}`);

        // Cache in localStorage if we have an internetMessageId (read mode)
        const internetMessageId = getInternetMessageId();
        if (internetMessageId) {
            localStorage.setItem(`parent_matter_${internetMessageId}`, JSON.stringify({
                matter,
                conversationId: conversationId,
                timestamp: Date.now()
            }));
        }

        // Cache using inReplyTo if in compose mode
        const inReplyTo = getInReplyToId();
        if (inReplyTo) {
            localStorage.setItem(`parent_matter_${inReplyTo}`, JSON.stringify({
                matter,
                conversationId: conversationId,
                timestamp: Date.now()
            }));
        }

        // In Compose mode, stamp the internet header so N8N fires on send
        if (surface === 'compose' || surface === 'ComposePane') {
            try {
                await setMatterInternetHeader(matter.id || matter.matter_id);
            } catch (err) {
                console.error('Failed to set internet header:', err);
            }
        }

        // Generate the conversationId natively from Drafts if it's a brand new email
        let activeConvId = conversationId;
        if ((surface === 'compose' || surface === 'ComposePane') && !activeConvId) {
            activeConvId = await saveDraftAndGetConversationId();
            if (activeConvId) setConversationId(activeConvId);
        }

        // Fire the link-matter webhook (best-effort, don't block UI on failure)
        try {
            await webhook.linkMatter({
                outlookItemId: itemId,
                conversationId: activeConvId,
                userEmail,
                senderEmail: senderInfo?.email || '',
                matterId: matter.matter_id || matter.id,
                matterNumber: matter.matter_number || matter.number,
                matterDescription: matter.matter_description || matter.name || '',
                source: surface,
                linkSource: 'manual',
            });
        } catch (err) {
            console.error('Failed to persist matter link:', err);
            // Don't un-link — the local state is enough for this session
        }
    }, [itemId, conversationId, userEmail, senderInfo, surface]);

    // ── Unlink Matter — remove the conversation→matter link ──
    const handleUnlinkMatter = useCallback(async () => {
        // Immediately clear local state
        setLinkedMatter(null);
        setLookupState('no_match');
        setStatusLabel('Matter unlinked');

        // Remove from localStorage
        const internetMessageId = getInternetMessageId();
        if (internetMessageId) {
            localStorage.removeItem(`parent_matter_${internetMessageId}`);
        }

        const inReplyTo = getInReplyToId();
        if (inReplyTo) {
            localStorage.removeItem(`parent_matter_${inReplyTo}`);
        }

        // Clear the internet header if in compose mode
        if (surface === 'compose' || surface === 'ComposePane') {
            try {
                await setMatterInternetHeader(null);
            } catch (err) {
                console.error('Failed to clear internet header:', err);
            }
        }

        // Persist the unlink (best-effort)
        try {
            await webhook.unlinkMatter({ conversationId, userEmail, surface });
        } catch (err) {
            console.error('Failed to persist matter unlink:', err);
        }
    }, [conversationId, userEmail, surface]);

    // ── Refresh Cache — sync matters from Clio to superset_db ──
    const handleRefreshCache = useCallback(async () => {
        if (!userEmail) return;
        setLookupState('loading');
        try {
            await webhook.refreshCache({ userEmail });
            // After refresh, force a re-lookup to get fresh results
            performLookup(itemId, conversationId, userEmail, senderInfo);
        } catch (err) {
            console.error('Cache refresh failed', err);
            setLookupState('error');
        }
    }, [userEmail, itemId, conversationId, senderInfo, performLookup]);

    // ── Re-lookup — re-trigger matter search ──
    const handleRelookup = useCallback(() => {
        setLinkedMatter(null);
        performLookup(itemId, conversationId, userEmail, senderInfo);
    }, [itemId, conversationId, userEmail, senderInfo, performLookup]);

    const handleLoginSuccess = () => {
        setAuthenticated(true);
        setIsAdminUser(isAdmin());
    };

    const handleLogout = () => {
        logout();
        setAuthenticated(false);
        setIsAdminUser(false);
        setShowAdmin(false);
    };

    // Show nothing while checking auth
    if (authChecking) {
        return (
            <div className="addin" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                <p style={{ fontFamily: 'Cabin, sans-serif', color: '#8a8a8a' }}>Loading…</p>
            </div>
        );
    }

    // Show login screen if not authenticated
    if (!authenticated) {
        return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
    }

    const contextValue = {
        surface,
        itemId,
        conversationId,
        userEmail,
        senderInfo,
        subject,
        linkedMatter,
        setLinkedMatter,
        lookupState,
        lookupMatters,
        handleLinkMatter,
        handleUnlinkMatter,
        handleRelookup,
        handleRefreshCache,
        status,
        setStatus,
        statusLabel,
        setStatusLabel,
        handleLogout,
        userSettings,
        setUserSettings,
    };

    // ── Settings Updates ──
    const updateSettings = async (newSettings) => {
        const merged = { ...userSettings, ...newSettings };
        setUserSettings(merged);
        await saveSettings(merged);
    };

    // Choose panel by surface
    let panelContent;

    if (showSettings) {
        panelContent = <SettingsPane onClose={() => setShowSettings(false)} onSave={updateSettings} />;
    } else if (showDashboard) {
        panelContent = <DashboardPane onClose={() => setShowDashboard(false)} />;
    } else if (showTriage) {
        panelContent = <TriagePane onClose={() => setShowTriage(false)} />;
    } else if (showAdmin) {
        panelContent = <AdminPane onClose={() => setShowAdmin(false)} />;
    } else if (surface === 'compose') {
        panelContent = <ComposePane />;
    } else {
        panelContent = <ReadingPane />;
    }

    return (
        <AddinContext.Provider value={contextValue}>
            <div className="addin">
                <Header 
                    onSettingsClick={() => { setShowSettings(!showSettings); setShowDashboard(false); setShowTriage(false); setShowAdmin(false); }} 
                    onDashboardClick={() => { setShowDashboard(!showDashboard); setShowSettings(false); setShowTriage(false); setShowAdmin(false); }}
                    onTriageClick={() => { setShowTriage(!showTriage); setShowSettings(false); setShowDashboard(false); setShowAdmin(false); }} 
                    onAdminClick={() => { setShowAdmin(!showAdmin); setShowSettings(false); setShowDashboard(false); setShowTriage(false); }}
                    isAdmin={isAdminUser}
                />
                {panelContent}
                <StatusBar label={statusLabel} />
            </div>
        </AddinContext.Provider>
    );
}
